import * as React from "react";
import { useLocation, useNavigate } from "react-router";

import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";

import type { OffsetResult, SessionWithRelations } from "@volley/data";

import logger from "../../log";
import fetchApi, { logFetchError } from "../../util/fetchApi";
import useDialog from "../Dialog/useDialog";
import { AuthState, useCurrentUser } from "../hooks/currentUser";
import { usePairingContext } from "../hooks/pairingStatus";
import { useStatus } from "../hooks/status";

import TopNavBar from "./TopNavBar";
import { trainerCompatibleWithSport, useSelectedSport } from "./context/sport";

const FIRST_SESSION_TOAST_KEY = "volley.hasSeenFirstSessionToast";

export default function UserPage({
    children,
}: React.PropsWithChildren): React.JSX.Element {
    const location = useLocation();
    const navigate = useNavigate();
    const { status } = useStatus();
    const { dialogType, setDialogType, batteryWarningDismissed } = useDialog();
    const { status: pairingStatus } = usePairingContext();
    const {
        selected: selectedSport,
        hasSelected,
        resolvingSelection,
    } = useSelectedSport();
    const { authState } = useCurrentUser();
    const [firstSessionToastOpen, setFirstSessionToastOpen] =
        React.useState(false);

    React.useEffect(() => {
        if (
            pairingStatus === "paired" &&
            (dialogType === "Device" || dialogType === null)
        ) {
            if (!trainerCompatibleWithSport(selectedSport, status)) {
                setDialogType("InvalidSportSelection");
            }
        }
    }, [pairingStatus, status, selectedSport, dialogType, setDialogType]);

    React.useEffect(() => {
        if (
            status?.trainer.battery.level &&
            status?.ready !== "INIT" &&
            status?.trainer.battery.level <= 30 &&
            dialogType !== "NoConfirmReplaceBatteries" &&
            !batteryWarningDismissed
        ) {
            setDialogType("LowBatteryWarning");
        }
    }, [status, batteryWarningDismissed, dialogType, setDialogType]);

    React.useEffect(() => {
        if (!hasSelected && !resolvingSelection) {
            logger.info("[UserPage] No sport selected, showing welcome dialog");
            setDialogType("SportSelectorWelcome");
        }
    }, [hasSelected, resolvingSelection, setDialogType]);

    React.useEffect(() => {
        if (authState !== AuthState.AUTHENTICATED) {
            return;
        }
        if (window.localStorage.getItem(FIRST_SESSION_TOAST_KEY)) {
            return;
        }
        fetchApi<OffsetResult<SessionWithRelations>>(
            "/api/sessions/me?limit=1&offset=0",
        )
            .then((recent) => {
                window.localStorage.setItem(FIRST_SESSION_TOAST_KEY, "true");
                if (recent.count === 0) {
                    logger.info(
                        "[UserPage] First session detected, showing tutorial toast",
                    );
                    setFirstSessionToastOpen(true);
                }
            })
            .catch(logFetchError);
    }, [authState]);

    // HACK: Force repaint after navigation.
    // iOS WebView/Safari fails to paint elements after back navigation due to
    // bfcache (back-forward cache) issues. The DOM is correct and interactive,
    // but not painted. We use a delayed viewport width change to force repaint.
    React.useEffect(() => {
        // Use a longer delay to ensure WebKit has finished bfcache restoration
        const timeoutId = setTimeout(() => {
            requestAnimationFrame(() => {
                window.scrollBy(-1, -1);
            });
        }, 50);

        return () => clearTimeout(timeoutId);
    }, [location.key]);

    return (
        <Box
            component="div"
            sx={{ backgroundColor: "primary.dark", minHeight: "100vh" }}
        >
            <TopNavBar />
            <Box component="main" sx={{ pl: { sm: "240px" } }}>
                {children}
            </Box>
            {/* No autoHideDuration: the welcome dialogs render on top of this
                toast, so it must wait for an explicit dismissal instead of
                timing out behind them. */}
            <Snackbar
                open={firstSessionToastOpen}
                onClose={(_event, reason) => {
                    if (reason === "clickaway") {
                        return;
                    }
                    setFirstSessionToastOpen(false);
                }}
                anchorOrigin={{ horizontal: "center", vertical: "top" }}
                sx={{ top: 60 }}
            >
                <Alert
                    severity="info"
                    action={
                        <>
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setFirstSessionToastOpen(false);
                                    navigate("/tutorials");
                                }}
                            >
                                Tour
                            </Button>
                            <IconButton
                                size="small"
                                aria-label="close"
                                color="inherit"
                                onClick={() => setFirstSessionToastOpen(false)}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </>
                    }
                >
                    New here? Take a quick tour, or find it any time in the
                    menu.
                </Alert>
            </Snackbar>
        </Box>
    );
}
